
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Trophy, Star, Calendar, DollarSign, Target, Crown } from 'lucide-react';

const AchievementSystem = () => {
  const achievements = [
    {
      id: 1,
      title: 'First Win',
      description: 'Win your first lottery prize',
      icon: <Trophy className="w-6 h-6 text-yellow-600" />,
      completed: true,
      reward: '+100 XP',
      category: 'milestones'
    },
    {
      id: 2,
      title: 'Lucky Streak',
      description: 'Win 3 games in a row',
      icon: <Star className="w-6 h-6 text-blue-600" />,
      completed: false,
      progress: 1,
      maxProgress: 3,
      reward: '+250 XP + Free Ticket',
      category: 'streaks'
    },
    {
      id: 3,
      title: 'Weekly Player',
      description: 'Play every day for a week',
      icon: <Calendar className="w-6 h-6 text-green-600" />,
      completed: false,
      progress: 4,
      maxProgress: 7,
      reward: '+150 XP',
      category: 'consistency'
    },
    {
      id: 4,
      title: 'Big Spender',
      description: 'Spend $100 in total',
      icon: <DollarSign className="w-6 h-6 text-purple-600" />,
      completed: true,
      reward: '+200 XP + 10% Bonus',
      category: 'spending'
    },
    {
      id: 5,
      title: 'Number Master',
      description: 'Match 5 numbers in a single game',
      icon: <Target className="w-6 h-6 text-red-600" />,
      completed: false,
      reward: '+500 XP + Premium Features',
      category: 'skill'
    },
    {
      id: 6,
      title: 'Golden Player',
      description: 'Reach level 10',
      icon: <Crown className="w-6 h-6 text-yellow-600" />,
      completed: false,
      progress: 7,
      maxProgress: 10,
      reward: 'VIP Status + Exclusive Games',
      category: 'progression'
    }
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');
  const categories = ['all', 'milestones', 'streaks', 'consistency', 'spending', 'skill', 'progression'];

  const filteredAchievements = selectedCategory === 'all' 
    ? achievements 
    : achievements.filter(achievement => achievement.category === selectedCategory);

  const completedCount = achievements.filter(a => a.completed).length;
  const userLevel = Math.floor(completedCount * 2) + 1;
  const userXP = completedCount * 150 + 50;

  return (
    <div className="space-y-6">
      {/* Player Stats */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Level {userLevel}</h3>
              <p className="text-gray-600">{userXP} XP</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Achievements</p>
              <p className="text-2xl font-bold text-yellow-600">{completedCount}/{achievements.length}</p>
            </div>
          </div>
          <Progress value={(completedCount / achievements.length) * 100} className="h-2" />
        </CardContent>
      </Card>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === category
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredAchievements.map((achievement) => (
          <Card key={achievement.id} className={`transition-all hover:shadow-md ${
            achievement.completed ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'
          }`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-full ${
                  achievement.completed ? 'bg-yellow-100' : 'bg-gray-100'
                }`}>
                  {achievement.icon}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-bold text-gray-900">{achievement.title}</h4>
                    {achievement.completed && (
                      <Badge className="bg-yellow-100 text-yellow-800">Completed</Badge>
                    )}
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3">{achievement.description}</p>
                  
                  {achievement.maxProgress && !achievement.completed && (
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Progress</span>
                        <span>{achievement.progress}/{achievement.maxProgress}</span>
                      </div>
                      <Progress value={(achievement.progress / achievement.maxProgress) * 100} className="h-2" />
                    </div>
                  )}
                  
                  <div className="text-xs text-gray-500">
                    Reward: {achievement.reward}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AchievementSystem;
